"use client";

import { useState } from "react";
import { useTranslations } from "@i18n/provider";
import { requestChatAboutFeedback } from "@lib/agent/chatAboutFeedback";
import { LessonAudioButton, type LessonAudioButtonState } from "@ui/LessonAudioButton";
import { SuccessBloom } from "./SuccessBloom";

export function SpeakingWaveform({ active }: { active: boolean }) {
  if (!active) return null;

  return (
    <div className="flex h-8 items-end justify-center gap-1" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className="w-1.5 rounded-full bg-speaking"
          style={{
            height: "100%",
            transformOrigin: "bottom",
            animation: `wave 0.9s ease-in-out ${i * 0.12}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function ChatAboutThisButton({
  context,
  onChatAboutThis,
}: {
  context?: string;
  onChatAboutThis?: () => void;
}) {
  const { t } = useTranslations();

  return (
    <button
      type="button"
      className="btn-ghost inline-flex items-center gap-2 text-sm"
      onClick={() => {
        onChatAboutThis?.();
        const trimmed = context?.trim();
        requestChatAboutFeedback({
          message: trimmed
            ? t("lesson.chatAboutFeedback.withContext", { context: trimmed })
            : t("lesson.chatAboutFeedback.plain"),
        });
      }}
    >
      <ChatIcon className="h-4 w-4" />
      {t("lesson.chatAboutThis")}
    </button>
  );
}

export function LessonFeedbackPanel({
  tone,
  title,
  body,
  action,
  showBloom = false,
  showChatAboutThis,
  chatAboutContext,
  onChatAboutThis,
  className,
  tutorAudio,
}: {
  tone: "success" | "gentle" | "neutral" | "empty";
  title?: string;
  body: React.ReactNode;
  action?: React.ReactNode;
  showBloom?: boolean;
  showChatAboutThis?: boolean;
  chatAboutContext?: string;
  onChatAboutThis?: () => void;
  className?: string;
  tutorAudio?: {
    state: LessonAudioButtonState;
    onPress: () => void;
  };
}) {
  const [bloomDismissed, setBloomDismissed] = useState(false);
  const showBloomEffect = showBloom && tone === "success" && !bloomDismissed;

  if (tone === "empty") {
    return <p className="feedback-empty">{body}</p>;
  }

  const panelClass =
    tone === "success"
      ? "feedback-success"
      : tone === "gentle"
        ? "feedback-gentle animate-sway"
        : "rounded-[20px] bg-surface-sunk p-4";

  const shouldShowChat =
    showChatAboutThis ?? (tone === "success" || tone === "gentle" || tone === "neutral");

  const footerAction =
    action ??
    (shouldShowChat ? (
      <ChatAboutThisButton context={chatAboutContext} onChatAboutThis={onChatAboutThis} />
    ) : null);

  return (
    <>
      {showBloomEffect ? <SuccessBloom onDone={() => setBloomDismissed(true)} /> : null}
      <div className={className ? `${panelClass} ${className}` : panelClass}>
        {title ? (
          <div className="mb-2 flex items-start gap-3">
            {tone === "success" || tone === "gentle" || tutorAudio ? (
              <div className="flex shrink-0 flex-col items-center gap-2">
                {tone === "success" ? (
                  <span className="flex h-[2.625rem] w-[2.625rem] shrink-0 items-center justify-center rounded-full bg-speaking text-lg text-white">
                    ✓
                  </span>
                ) : tone === "gentle" ? (
                  <span className="mt-[0.875rem] h-2.5 w-2.5 shrink-0 rounded-full bg-writing animate-breathe" />
                ) : null}
                {tutorAudio ? (
                  <LessonAudioButton
                    state={tutorAudio.state}
                    onClick={tutorAudio.onPress}
                    variant={tone === "success" ? "speaking" : "reading"}
                  />
                ) : null}
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg font-bold text-ink">{title}</p>
              <div className="mt-1 text-sm leading-relaxed text-ink-body">{body}</div>
            </div>
          </div>
        ) : (
          <div className="text-sm leading-relaxed text-ink-body">{body}</div>
        )}
        {footerAction ? <div className="mt-4">{footerAction}</div> : null}
      </div>
    </>
  );
}
